import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { format, addMonths, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import { Timesheet, TimesheetEntry } from '../services/timesheets';
import { Client } from '../services/clients';

interface TimesheetMonthViewProps {
  timesheets: Timesheet[];
  clients: Client[];
  onDateClick?: (date: Date, entries: TimesheetEntry[]) => void;
  currentMonth?: Date;
  onMonthChange?: (month: Date) => void;
}

type DateStatus = {
  date: Date;
  entries: TimesheetEntry[];
  status: 'none' | 'draft' | 'submitted' | 'approved' | 'invoiced';
  totalHours: number;
  clientNames: string[];
};

const TimesheetMonthView: React.FC<TimesheetMonthViewProps> = ({
  timesheets,
  clients,
  onDateClick,
  currentMonth = new Date(),
  onMonthChange,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const getClientName = (clientId: string): string => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = direction === 'prev' 
      ? addMonths(selectedMonth, -1)
      : addMonths(selectedMonth, 1);
    setSelectedMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  // Build a map of dates to their status
  const getDateStatuses = (): Map<string, DateStatus> => {
    const statusMap = new Map<string, DateStatus>();

    // Process all timesheets and their entries
    timesheets.forEach((timesheet) => {
      timesheet.entries.forEach((entry) => {
        const entryDate = parseISO(entry.date);
        const dateKey = format(entryDate, 'yyyy-MM-dd');
        
        if (!statusMap.has(dateKey)) {
          statusMap.set(dateKey, {
            date: entryDate,
            entries: [],
            status: 'none',
            totalHours: 0,
            clientNames: [],
          });
        }

        const dateStatus = statusMap.get(dateKey)!;
        dateStatus.entries.push(entry);
        dateStatus.totalHours += entry.hours;
        
        // Add unique client names
        const clientName = getClientName(entry.clientId);
        if (!dateStatus.clientNames.includes(clientName)) {
          dateStatus.clientNames.push(clientName);
        }

        // Determine status based on timesheet status (highest priority wins)
        // Priority: invoiced > approved > submitted > draft
        if (timesheet.status === 'invoiced' && dateStatus.status !== 'invoiced') {
          dateStatus.status = 'invoiced';
        } else if (timesheet.status === 'approved' && dateStatus.status !== 'invoiced' && dateStatus.status !== 'approved') {
          dateStatus.status = 'approved';
        } else if (timesheet.status === 'submitted' && dateStatus.status !== 'invoiced' && dateStatus.status !== 'approved' && dateStatus.status !== 'submitted') {
          dateStatus.status = 'submitted';
        } else if (timesheet.status === 'draft' && dateStatus.status === 'none') {
          dateStatus.status = 'draft';
        }
      });
    });

    return statusMap;
  };

  const dateStatuses = getDateStatuses();

  const getDateColor = (status: DateStatus['status']): string => {
    switch (status) {
      case 'invoiced':
        return '#9e9e9e'; // Grey
      case 'approved':
        return '#4caf50'; // Green
      case 'submitted':
      case 'draft':
        return '#9c27b0'; // Purple
      default:
        return 'transparent';
    }
  };

  const getDateBgColor = (status: DateStatus['status']): string => {
    switch (status) {
      case 'invoiced':
        return '#f5f5f5'; // Light grey background
      case 'approved':
        return '#e8f5e9'; // Light green background
      case 'submitted':
      case 'draft':
        return '#f3e5f5'; // Light purple background
      default:
        return 'transparent';
    }
  };

  const isDateDisabled = (status: DateStatus['status']): boolean => {
    return status === 'invoiced';
  };

  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const daysInMonth = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const handleDateClick = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const dateStatus = dateStatuses.get(dateKey);
    
    if (dateStatus && !isDateDisabled(dateStatus.status)) {
      onDateClick?.(date, dateStatus.entries);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Month Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <IconButton onClick={() => navigateMonth('prev')}>
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h6">
          {format(selectedMonth, 'MMMM yyyy')}
        </Typography>
        <IconButton onClick={() => navigateMonth('next')}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      {/* Weekday Headers */}
      <Grid container spacing={0.5} sx={{ mb: 1 }}>
        {weekDays.map((day) => (
          <Grid item xs={12 / 7} key={day}>
            <Typography variant="caption" align="center" fontWeight="bold">
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Grid container spacing={0.5}>
        {daysInMonth.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dateStatus = dateStatuses.get(dateKey);
          const isCurrentMonth = isSameMonth(day, selectedMonth);
          const isToday = isSameDay(day, new Date());
          const isDisabled = dateStatus ? isDateDisabled(dateStatus.status) : false;
          const bgColor = dateStatus ? getDateBgColor(dateStatus.status) : 'transparent';
          const borderColor = dateStatus ? getDateColor(dateStatus.status) : 'transparent';
          const hasEntries = dateStatus && dateStatus.entries.length > 0;

          return (
            <Grid item xs={12 / 7} key={day.toISOString()}>
              <Tooltip
                title={
                  dateStatus
                    ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {dateStatus.totalHours.toFixed(1)} hours - {dateStatus.status}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {dateStatus.entries.length} entry(ies)
                          </Typography>
                          {dateStatus.clientNames.length > 0 && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              Clients: {dateStatus.clientNames.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      )
                    : 'No entries'
                }
                arrow
              >
                <Box
                  onClick={() => !isDisabled && handleDateClick(day)}
                  sx={{
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    cursor: isDisabled ? 'not-allowed' : hasEntries ? 'pointer' : 'default',
                    borderRadius: '4px',
                    backgroundColor: bgColor,
                    border: hasEntries ? `2px solid ${borderColor}` : isToday ? '2px solid #1976d2' : '1px solid transparent',
                    borderColor: hasEntries ? borderColor : isToday ? '#1976d2' : 'transparent',
                    opacity: isDisabled ? 0.5 : 1,
                    color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: isDisabled 
                        ? bgColor 
                        : hasEntries 
                        ? bgColor 
                        : 'action.hover',
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    fontWeight={isToday ? 'bold' : 'normal'}
                    sx={{ alignSelf: 'flex-start', mb: 0.5 }}
                  >
                    {format(day, 'd')}
                  </Typography>
                  {hasEntries && dateStatus && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {dateStatus.totalHours.toFixed(1)}h
                      </Typography>
                      {dateStatus.clientNames.length > 0 && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.65rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {dateStatus.clientNames.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Legend */}
      <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
        <Chip label="Draft/Submitted" size="small" sx={{ bgcolor: '#f3e5f5', border: '1px solid #9c27b0' }} />
        <Chip label="Approved" size="small" sx={{ bgcolor: '#e8f5e9', border: '1px solid #4caf50' }} />
        <Chip label="Invoiced" size="small" sx={{ bgcolor: '#f5f5f5', border: '1px solid #9e9e9e', opacity: 0.5 }} />
      </Box>
    </Paper>
  );
};

export default TimesheetMonthView;







